import { auth, db } from '@src/firebase/firebase';
import { collection, doc } from 'firebase/firestore';
import type { Moment } from 'moment';

import type { TEventTypeSummary } from '@src/models/interfaces/eventTypeSummary.interface';
import type TScheduleTrigger from '@src/models/interfaces/scheduleTrigger.interface';
import type {
  WorkTemplateFormValues,
  WorkTemplateUpsertPayload,
} from '@src/models/interfaces/scheduleTemplate.interface';
import { EmbeddedAuditMapper } from '@src/models/mappers/commons/embeddedAuditMapper';
import { convertDateToMinutes } from '@src/packages/utils';
import { addOrderToTriggers, removeOrderFromTriggers } from '@src/packages/utils/functions/triggers';
import { PATHS as WORK_ORDER_PATHS } from '@src/packages/workOrders/constants';
import { CustomError } from '@src/util/customError';

export const BASE_URL = 'work_templates';

export const PATHS = {
  BASE_URL,
  CREATE_TEMPLATE: `${BASE_URL}/create`,
  EDIT_TEMPLATE: `${BASE_URL}/template/:divId/:templateId`,
} as const;

export const FORM_ITEM_LAYOUT = {
  labelCol: {
    span: 6,
  },
  wrapperCol: {
    span: 24,
  },
};

export const FORMS = {
  WORK_TEMPLATE: 'workTemplate',
  FORMS: 'forms',
  TRIGGERS: 'triggers',
} as const;

export const COMPONENTS_NOT_ALLOWED = ['file', 'video'] as const;
export const MAX_FORMS = 5;
export const MAX_TEMPLATE_TRIGGERS = 20;
export const PAGE_SIZE = 50;

export const getRouteToCreateWorkTemplate = (): string => PATHS.CREATE_TEMPLATE;

export const getRouteToEditWorkTemplate = (divId: string, templateId: string): string =>
  PATHS.EDIT_TEMPLATE.replace(':divId', divId).replace(':templateId', templateId);

export const getRouteToCreateWorkOrder = (divId: string, templateId: string): string =>
  WORK_ORDER_PATHS.CREATE_ORDER_BY_TEMPLATE.replace(':divId', divId).replace(
    ':templateId',
    templateId,
  );

interface FactoryWorkTemplateParams {
  data: WorkTemplateFormValues;
  divisionId: string;
  forms?: TEventTypeSummary[];
  triggers?: TScheduleTrigger[];
}

export const factoryWorkTemplate = ({
  data,
  divisionId,
  forms = [],
  triggers = [],
}: FactoryWorkTemplateParams): WorkTemplateUpsertPayload => {
  try {
    const {
      name,
      startDateOptional,
      startTimeOptional,
      duration,
      durationReadonly,
      timezone,
      timezoneReadonly,
      minUsers,
      isExpiredAllowed,
      expiredAllowedTime,
      triggersFollowIndex,
    } = data;

    let triggersParsed = triggers;
    if (triggersFollowIndex) {
      triggersParsed = addOrderToTriggers(triggers);
    } else {
      triggersParsed = removeOrderFromTriggers(triggers);
    }
    // Templates are written straight to Firestore: audit fields on embedded
    // location copies must be real Timestamps (or absent), never serialized maps.
    triggersParsed = triggersParsed.map(trigger =>
      EmbeddedAuditMapper.normalizeTriggerEmbeddedLocations(trigger),
    );

    return {
      forms,
      name,
      startDateOptional,
      startTimeOptional,
      duration,
      durationReadonly,
      triggers: triggersParsed,
      divId: divisionId,
      timezone,
      timezoneReadonly,
      minUsers,
      allowedGroups: [],
      isExpiredAllowed,
      expiredAllowedTime: convertDateToMinutes(expiredAllowedTime),
      triggersFollowIndex: Boolean(triggersFollowIndex),
    };
  } catch (error) {
    const err = error as { message?: string; code?: string };
    throw new CustomError(err?.message, err?.code);
  }
};

export const factoryTriggerToWorkTemplateExisting = (
  data: TScheduleTrigger[],
): TScheduleTrigger[] =>
  data.map(item => ({
    ...item,
    locationOptional: true,
    locationRequired: false,
    expectedExecutions: 1,
  }));

interface TriggerFormLocation {
  isAGroup?: boolean;
  data?: unknown;
}

interface TriggerFormEventType extends Partial<TEventTypeSummary> {
  title?: string;
  key?: string;
  label?: string;
  disabled?: boolean;
  value?: string;
}

interface TriggerFormValues {
  cooldown?: Moment | null;
  description?: string;
  eventType?: TriggerFormEventType;
  name?: string;
  location?: TriggerFormLocation;
  locationOptional?: boolean;
  locationRequired?: boolean;
}

export const factoryTriggerToWorkTemplate = (
  data: TriggerFormValues | undefined,
  divisionId: string,
): Record<string, unknown> => {
  const { cooldown, description, eventType, name, location, locationOptional, locationRequired } =
    data || {};

  const locationData = location?.data;
  const {
    title: _eventTypeTitle,
    key: _eventTypeKey,
    label: _eventTypeLabel,
    disabled: _eventTypeDisabled,
    value: _eventTypeValue,
    ...newEventType
  } = eventType || {};

  const cooldownInMinutes = cooldown ? convertDateToMinutes(cooldown) : null;

  const uniqueId = doc(collection(db, 'triggers'));

  const triggerBody: Record<string, unknown> = {
    avoidConsecutiveTriggering: false,
    cooldown: cooldownInMinutes,
    description,
    eventType: newEventType as TEventTypeSummary,
    id: uniqueId.id,
    name: name || '',
    locationOptional: Boolean(locationOptional),
    locationRequired: Boolean(locationRequired),
    uid: auth.currentUser?.uid,
    divId: divisionId,
    expectedExecutions: 1,
  };

  if (location?.isAGroup) {
    triggerBody.locationGroup = locationData;
  } else {
    triggerBody.locations = locationData || [];
  }

  return triggerBody;
};

export const mergeTriggers = (
  triggers: TScheduleTrigger[],
  newTriggers: TScheduleTrigger[],
): TScheduleTrigger[] =>
  triggers.reduce<TScheduleTrigger[]>((acc, item) => {
    const existingItem = acc.find(
      i => (i?.triggerId && i?.triggerId === item?.triggerId) || i?.id === item?.id,
    );
    if (!existingItem) {
      acc.push({
        ...item,
      });
    }
    return acc;
  }, newTriggers);
